import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import Layout from "../../components/Layout";
import { get } from "../../services/ApiService";
import { Toaster } from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import { Modal, Button, Badge, Tooltip, Select, Space } from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  ApartmentOutlined,
  FilterOutlined,
  PlusOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import AddEventForm from "./AddEventForm";
import "./index.css";

const { Option } = Select;

// Initialize localizer
moment.updateLocale("en", {
  week: {
    dow: 1, // 1 represents Monday as the start of the week
  },
});

const localizer = momentLocalizer(moment);

// Custom Event Component with the exact format you requested
const CustomEvent = ({ event }) => {
  const formatTime = (date) => {
    return moment(date).format("HH:mm");
  };

  const getStatus = () => {
    if (event.isOff) return "CLOSED";
    return "OPEN";
  };

  // Color scheme based on division and status
  const getEventColors = () => {
    if (event.isOff) {
      return {
        bg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
        border: "#b91c1c",
      };
    }

    switch (event.division) {
        case "PHARMA":
        return {
            bg: "linear-gradient(135deg, #4e7661 0%, #4e7661 100%)",
            border: "#4e7661",
        };

        case "CLINICAL":
        return {
            bg: "linear-gradient(135deg, #e2b9b3 0%, #e2b9b3 100%)",
            border: "#e2b9b3",
        };
      default:
        return {
          bg: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
          border: "#374151",
        };
    }
  };

  const colors = getEventColors();

  return (
    <Tooltip
      title={
        <div style={{ padding: "4px" }}>
          <div>
            <strong>{event.title}</strong>
          </div>
          <div>Status: {getStatus()}</div>
          <div>Division: {event.division}</div>
          <div>
            Time: {formatTime(event.start)} - {formatTime(event.end)}
          </div>
          <div>Users: {event.userNames?.join(", ") || "No users assigned"}</div>
        </div>
      }
    >
      <div
        style={{
          padding: "6px 8px",
          borderRadius: "6px",
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          color: "white",
          height: "100%",
          cursor: "pointer",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
          display: "flex",
          flexDirection: "column",
          fontSize: "11px",
        }}
        className="custom-event"
      >
        {/* Title Row with Status */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "4px",
            fontWeight: "600",
            fontSize: "11px",
          }}
        >
          {/* <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {event.title}
          </span> */}
          <span
            style={{
              background: "rgba(255,255,255,0.2)",
              padding: "2px 6px",
              borderRadius: "4px",
              backdropFilter: "blur(4px)",
              fontSize: "9px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              marginLeft: "4px",
              whiteSpace: "nowrap",
            }}
          >
            {getStatus()}
          </span>
        </div>

        {/* Time Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            marginBottom: "4px",
            fontSize: "10px",
            background: "rgba(0,0,0,0.2)",
            padding: "2px 6px",
            borderRadius: "4px",
            width: "fit-content",
          }}
        >
          <ClockCircleOutlined style={{ fontSize: "9px" }} />
          <span>
            {formatTime(event.start)} - {formatTime(event.end)}
          </span>
        </div>

        {/* Division Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            marginBottom: "4px",
            fontWeight: "500",
            fontSize: "10px",
          }}
        >
          <ApartmentOutlined style={{ fontSize: "9px" }} />
          <span>{event.division}</span>
        </div>

        {/* Users Row */}
        {event.userNames && event.userNames.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "9px",
              opacity: 0.9,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <UserOutlined style={{ fontSize: "8px" }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {event.userNames.join(", ")}
            </span>
          </div>
        )}
      </div>
    </Tooltip>
  );
};

const Event = () => {
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [filterDivision, setFilterDivision] = useState(null);
  const [calendarView, setCalendarView] = useState("month");

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const res = await get(`api/shifts/event/list`);
      setIsLoading(false);
      if (res.success) {
        setEvents(res.events);
        setUsers(res.users);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const handleEventClick = (event) => {
    const foundEvent = events.find((e) => e._id === event.shiftId);
    if (foundEvent) {
      setSelectedEvent(foundEvent);
      setIsModalOpen(true);
    }
  };

  const handleDateChange = (event) => {
    const selectedValue = event.target.value;
    setFilterDate(selectedValue ? selectedValue : null);
  };

  const handleViewChange = (view) => {
    setCalendarView(view);
  };

  const handleSlotSelect = ({ start }) => {
    setSelectedDate(start);
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  // Filter events based on selected date and division
  const filteredEvents = events
    .filter((event) => {
      if (filterDivision && event.division !== filterDivision) {
        return false;
      }

      if (filterDate) {
        const eventStart = moment(
          `${event.startDate} ${event.startTime || "00:00"}`,
          "YYYY-MM-DD HH:mm",
        ).startOf("day");
        const eventEnd = moment(
          `${event.endDate || event.startDate} ${event.endTime || "23:59"}`,
          "YYYY-MM-DD HH:mm",
        ).endOf("day");
        const selectedMoment = moment(filterDate);

        return (
          eventStart.isSame(selectedMoment, "day") ||
          eventEnd.isSame(selectedMoment, "day")
        );
      }
      return true;
    })
    .map((event) => {
      const isAllDay = !event.startTime && !event.endTime;
      const start = isAllDay
        ? moment(event.startDate, "YYYY-MM-DD").toDate()
        : moment(
            `${event.startDate} ${event.startTime}`,
            "YYYY-MM-DD HH:mm",
          ).toDate();

      const end = isAllDay
        ? moment(event.endDate || event.startDate, "YYYY-MM-DD").toDate()
        : moment(
            `${event.endDate || event.startDate} ${event.endTime}`,
            "YYYY-MM-DD HH:mm",
          ).toDate();

      // Get user names for the event
      const eventUsers = users.filter((user) =>
        event.userIds?.includes(user._id),
      );
      const userNames = eventUsers.map((user) => user.firstname);

      return {
        title: event.eventName,
        allDay: isAllDay,
        start,
        end,
        shiftId: event._id,
        isOff: event.isOff,
        division: event.division,
        userNames,
        userCount: event.userIds?.length || 0,
      };
    });

  return (
    <>
      <Layout>
        <Toaster position="top-center" reverseOrder={false} />

        {/* Header Section */}
        <div className="row mb-6 align-items-center">
          <div className="col-md-6">
            <div className="d-flex align-items-center">
              <div>
                <h2 className="fs-2 fw-bold mb-1">Events Calendar</h2>
                <Breadcrumbs />
              </div>
            </div>
          </div>
          <div className="col-md-6 text-end">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => {
                setSelectedEvent(null);
                setIsModalOpen(true);
              }}
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                border: "none",
                boxShadow: "0 2px 4px rgba(59,130,246,0.3)",
              }}
            >
              Add New Event
            </Button>
          </div>
        </div>

        {/* Filter Section */}
        <div
          className="card mb-4 rounded-4 shadow-sm"
          style={{ border: "1px solid #e5e7eb" }}
        >
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <input
                  type="date"
                  className="form-control form-control-lg"
                  value={filterDate || ""}
                  onChange={handleDateChange}
                  style={{
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    padding: "10px 12px",
                  }}
                />
              </div>
              <div className="col-md-6 text-end">
                <div className="d-flex gap-4">
                  <div className="d-flex align-items-center">
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        background:
                          "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                        borderRadius: "4px",
                        marginRight: "8px",
                      }}
                    />
                    <span className="text-muted">PHARMA</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        borderRadius: "4px",
                        marginRight: "8px",
                      }}
                    />
                    <span className="text-muted">CLINICAL</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        background:
                          "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                        borderRadius: "4px",
                        marginRight: "8px",
                      }}
                    />
                    <span className="text-muted">CLOSED</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend with new colors */}
          </div>
        </div>

        {/* Calendar Section */}
        <div
          className="card rounded-4 shadow-sm"
          style={{ border: "1px solid #e5e7eb" }}
        >
          <div className="card-body p-4">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : filteredEvents.length > 0 ? (
              <Calendar
                localizer={localizer}
                events={filteredEvents}
                onSelectEvent={handleEventClick}
                onSelectSlot={handleSlotSelect}
                selectable
                startAccessor="start"
                endAccessor="end"
                view={calendarView}
                onView={handleViewChange}
                views={["month", "week", "day", "agenda"]}
                components={{
                  event: CustomEvent,
                }}
                step={30}
                timeslots={2}
                min={new Date(2024, 0, 1, 6, 0)}
                max={new Date(2024, 0, 1, 22, 0)}
                style={{ height: 700 }}
                className="custom-calendar"
                popup
                tooltipAccessor={null}
              />
            ) : (
              <div className="text-center py-5">
                <CalendarOutlined
                  style={{
                    fontSize: "48px",
                    color: "#9ca3af",
                    marginBottom: "16px",
                  }}
                />
                <h4 className="text-muted">No events found</h4>
                <p className="text-muted mb-3">
                  Try adjusting your filters or create a new event
                </p>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedEvent(null);
                    setIsModalOpen(true);
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                    border: "none",
                  }}
                >
                  Create Event
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        <Modal
          title={
            <div style={{ fontSize: "18px", fontWeight: "600" }}>
              {selectedEvent ? (
                <>
                  <CalendarOutlined
                    style={{ marginRight: "8px", color: "#3b82f6" }}
                  />
                  Edit Event
                </>
              ) : (
                <>
                  <PlusOutlined
                    style={{ marginRight: "8px", color: "#3b82f6" }}
                  />
                  Add New Event
                </>
              )}
            </div>
          }
          open={isModalOpen}
          onCancel={handleModalClose}
          footer={null}
          width={600}
          destroyOnClose
        >
          <AddEventForm
            selectedDate={selectedDate}
            onClose={handleModalClose}
            onEventAdded={fetchEvents}
            users={users}
            existingEvent={selectedEvent}
            allEvents={events}
          />
        </Modal>
      </Layout>
    </>
  );
};

export default Event;
